import React, { useState, useRef, useEffect } from 'react';
import SummaryApi from '../common';
import { FaPenNib } from 'react-icons/fa6';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const CreateBlog = () => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description1: '',
    description2: '',
    category: '',
    is_active: true,
  });
const navigate = useNavigate();

  const [blogImage, setBlogImage] = useState(null);
  const [blogVideo, setBlogVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const imageRef = useRef(null);
  const videoRef = useRef(null);

  // Theme detection - follow admin-theme from localStorage and listen for changes
  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem('admin-theme');
      const shouldBeDark = adminTheme === 'dark';
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
    };

    // Initial theme setup
    updateTheme();

    // Listen for theme changes from AdminPanel
    const handleThemeChange = () => {
      updateTheme();
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'admin-theme') {
        updateTheme();
      }
    };

    // Listen for custom admin theme events
    const handleAdminThemeToggled = () => {
      updateTheme();
    };

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminThemeToggled', handleAdminThemeToggled);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminThemeToggled', handleAdminThemeToggled);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };
const getTodayDate = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    const fullData = { ...formData, blog_date: getTodayDate() };

    Object.entries(fullData).forEach(([key, value]) => {
      data.append(key, key === 'is_active' ? (value ? 1 : 0) : value);
    });

    if (blogImage) data.append('blog_image', blogImage);
    if (blogVideo) data.append('blog_video', blogVideo);

    try {
      const response = await fetch(SummaryApi.createBlog.url, {
        method: 'POST',
        body: data,
      });

 if (response.ok) {
  await Swal.fire({
    icon: 'success',
    title: 'Blog Created',
    text: 'Your blog has been successfully created!',
    background: isDarkMode ? '#1f2937' : '#fff',
    color: isDarkMode ? '#fff' : '#000',
  });

  // Redirect to blog report page
  navigate('/admin-panel/blog-report');
}
 else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to create the blog. Please try again.',
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#fff' : '#000',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'You had very bad network connection.',
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#fff' : '#000',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center mt-[-5%] transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
    }`}>
      <div className={`rounded-lg w-full max-w-xl p-6 transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gray-800 shadow-lg shadow-gray-900/50' 
          : 'bg-white shadow-md'
      }`}>
        <div className="flex items-center gap-2 mb-6">
          <FaPenNib className={`text-xl ${
            isDarkMode ? 'text-blue-400' : 'text-blue-600'
          }`} />
          <h2 className={`text-xl font-bold ${
            isDarkMode ? 'text-blue-400' : 'text-blue-700'
          }`}>
            Create Blog
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className={`block font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className={`w-full p-2 border rounded transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500' 
                  : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Enter blog title"
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className={`block font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Subtitle <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              required
              className={`w-full p-2 border rounded transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500' 
                  : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Enter blog subtitle"
            />
          </div>

          {/* Category */}
          <div>
            <label className={`block font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Category
            </label>
            <input
              type="text"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`w-full p-2 border rounded transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500' 
                  : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Enter category"
            />
          </div>

          {/* Description 1 */}
          <div>
            <label className={`block font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Description 1 <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description1"
              value={formData.description1}
              onChange={handleChange}
              required
              rows="4"
              className={`w-full p-2 border rounded transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500' 
                  : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Enter main description"
            />
          </div>

          {/* Description 2 */}
          <div>
            <label className={`block font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Description 2
            </label>
            <textarea
              name="description2"
              value={formData.description2}
              onChange={handleChange}
              rows="4"
              className={`w-full p-2 border rounded transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500' 
                  : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Enter additional description (optional)"
            />
          </div>

          {/* Image */}
          <div>
            <label className={`block font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Blog Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setBlogImage(e.target.files[0])}
              ref={imageRef}
              className={`w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold transition-colors duration-300 ${
                isDarkMode
                  ? 'file:bg-blue-600 file:text-white hover:file:bg-blue-700 text-gray-300'
                  : 'file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 text-gray-700'
              }`}
            />
          </div>

          {/* Video */}
          <div>
            <label className={`block font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Blog Video
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setBlogVideo(e.target.files[0])}
              ref={videoRef}
              className={`w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold transition-colors duration-300 ${
                isDarkMode
                  ? 'file:bg-blue-600 file:text-white hover:file:bg-blue-700 text-gray-300'
                  : 'file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 text-gray-700'
              }`}
            />
          </div>

          {/* Toggle Switch */}
          <div className="flex items-center justify-between">
            <label className={`block font-medium ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Active
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className={`w-11 h-6 rounded-full peer transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-600 peer-checked:bg-green-500' 
                  : 'bg-gray-300 peer-checked:bg-green-600'
              }`} />
              <div className={`absolute left-[5px] top-1 w-4 h-4 rounded-full shadow transform peer-checked:translate-x-full transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-gray-300 peer-checked:bg-white' 
                  : 'bg-white peer-checked:bg-gray-100'
              }`} />
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded text-white font-medium transition-all duration-300 ${
              loading
                ? 'bg-blue-400 cursor-not-allowed'
                : isDarkMode
                  ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30'
                  : 'bg-blue-900 hover:bg-blue-800 shadow-lg shadow-blue-700/30'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Submitting...
              </div>
            ) : (
              'Submit'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateBlog;